/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './score.module.css' with { type: 'css' };
import html from './score.module.html';
import { MusicTrace, Note, TraceEvent, TunePadAudio, toNum } from '../../audio';
import Vex from 'vexflow';


/*
TESTS

notes = [ 48, 50, 52, 53, 55, 57, 59, 60 ]
for note in notes:
    playNote(note)

notes = [ 48, 50, 52, 53, 55, 57, 59, 60 ]
for note in notes:
    playNote([note, note + 3, note + 6 ])

notes = [ 48, 50, 52, 53, 55, 57, 59, 60 ]
for note in notes:
    playNote(note, 2.5)

notes = [ 48, 50, 52, 53, 55, 57, 59, 60 ]
for note in notes:
    playNote(note, 10)

notes = [ 48, 50, 52, 53, 55, 57, 59, 60 ] * 3
for note in notes:
    playNote(note, 1.22)
*/


/**
 * Draws music notation given a MusicTrace object
 */
export class TimelineScore extends HTMLElement {

    static readonly ELEMENT = "timeline-score";

    static observedAttributes = [
        "playhead",    // location of the playhead in beats
        "instrument",  // "true" | "false"
    ];

    /** all of the HTML elements are contained within a shadow DOM */
    private root : ShadowRoot;

    /** inner and outer container that allow for horizontal scrolling */
    private container : HTMLDivElement;
    private scrollContainer : HTMLDivElement;

    /** dimensions of outer scroll container (will get resized on compontent load) */
    private width = 700;
    private height = 110;

    /** TunePad audio engine */
    private audio : TunePadAudio;

    /** hold on to the generated notes so that we can step through notes with the playhead */
    private notes : NoteRecord[] = [];
    
    /** carries note fragments from one measure to the next */
    private remainders : TraceEvent[] = [];

    // used to automatically select a clef based on note range
    private clef : string = 'treble';

    // maps time (in beats) to x-coordinate in pixels
    private beatmap = new Map<number, number>();

    private get isPercussion() : boolean { return this.getAttribute('instrument')?.startsWith('drum') || false; }

    /** trace object used to generate score. when this property is updated, the score will rerender */
    private trace : MusicTrace = MusicTrace.emptyTrace();

    /** total beat count for the current trace rounded up to the nearest measure */
    private get beats() {
        const b = this.trace.beats;
        const measure = this.audio.beatsPerMeasure;
        const bars = Math.ceil(b / measure);
        return Math.max(measure, bars * measure);
    }


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
        this.audio = TunePadAudio.init();
        this.container = this.root.querySelector('#container') as HTMLDivElement;
        this.scrollContainer = this.root.querySelector('#scroll-container') as HTMLDivElement;
    }

    public setTrace(t : MusicTrace, force : boolean = false) {
        const changed = !t.isEquivalent(this.trace);
        this.trace = t;
        if (changed || force) this.render();
    }


    connectedCallback() {
        window.addEventListener('resize', (e) => this.render());

        let down = false;
        this.container.addEventListener('pointerdown', e => {
            this.movePlayhead(this.xToBeat(e.clientX) + 1/64);
            this.emitEvent('playhead-reposition');
            down = true;
        });
        document.addEventListener('pointerup', (e) => {
            if (down) this.emitEvent('playhead-up');
            down = false;
        });
        this.audio.addSubscriber(this);
        this.render();
    }


    disconnectedCallback() {
        this.audio.removeSubscriber(this);
    }


    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (name === 'playhead' && newValue !== oldValue) {
            this.movePlayhead(toNum(newValue, 0));
        } else if (name === 'instrument' && newValue !== oldValue) {
            this.render();
        }
    }


    onClockReset() { }
    onClockTimeChange() { }
    onTempoChange() { }
    onTimeSignatureChange() { this.render(); }
    onKeyChange() { this.render(); }


    private emitEvent(name : string, tevent? : TraceEvent) {
        this.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            composed: true,
            detail: { playhead : this.playhead, note : tevent }
        }));
    }



    /**
     * Take a MusicTrace and render sheet music using Vex.
     */
    public render() {
        this.width = this.root.host.getBoundingClientRect().width;
        this.height = this.root.host.getBoundingClientRect().height;
        this.scrollContainer.style.width = `${this.width}px`;

        const trace = this.trace;
        this.notes = [];
        this.beatmap.clear();
        this.clef = this.isPercussion ? 'percussion' : bestClef(trace);
        try {
            this._render(trace);
        } catch (e) {
            console.log(e);  // perhaps handle this more gracefully
        }
    }



    private _render(trace : MusicTrace) {
        this.container.innerHTML = '';

        const MARGIN = 10;
        const BAR_WIDTH = 220;

        const audio = TunePadAudio.init();
        const beatCount = audio.beatsPerMeasure * (4 / audio.beatValue);
        const barCount = Math.ceil(trace.beats / beatCount);
        const renderer = new Vex.Renderer(this.container, Vex.Renderer.Backends.SVG);
        const formatter = new Vex.Formatter();
        const context = renderer.getContext();

        let minY = 220, maxY = 0;
        let sx = MARGIN;

        // if the trace is empty, add an empty stave
        if (barCount === 0) {
            const stave = new Vex.Stave(MARGIN, 0, BAR_WIDTH * 2 + MARGIN);
            stave.addClef(this.clef);
            stave.addTimeSignature(audio.meter);
            if (!this.isPercussion) {
                stave.addKeySignature(audio.key.signature);
            }
            stave.setContext(context).draw();
            sx += BAR_WIDTH * 2 + MARGIN;
        }
        
        // leftover notes parts that carry over to the next measure with a tie
        this.remainders = [];

        // loop through each bar
        for (let bar = 0; bar < barCount; bar++) {

            //---------------------------------------------------------
            // STEP 1: gather all notes from the current measure
            //---------------------------------------------------------
            const subtrace = trace
                .subtrace(bar * beatCount, (bar + 1) * beatCount)
                .ofType([ TraceEvent.PLAY, TraceEvent.REST, TraceEvent.SAMPLE ])
                .join(this.remainders)
                .sort((a, b) => (a.time === b.time) ? (a.trace_group - b.trace_group) : (a.time - b.time));

            this.remainders = [];

            //---------------------------------------------------------
            // STEP 2: generate voice object for each voice in the measure
            //---------------------------------------------------------
            const voices : any[] = [ ];
            for (const vid of subtrace.voices) {
                const records = this.generateVoice(
                    subtrace.filter(e => e.voice === vid),
                    audio.beatsPerMeasure,
                    audio.beatValue
                );
                this.notes.push( ... records);
                const voice = new Vex.Voice({ 
                    numBeats: audio.beatsPerMeasure,
                    beatValue: audio.beatValue 
                });
                voice.addTickables(records.map(r => r.vnote));
                voices.push(voice);
            }
            formatter.joinVoices(voices);
            formatter.preCalculateMinTotalWidth(voices);
            let swidth = Math.max(formatter.getMinTotalWidth(), BAR_WIDTH);
            formatter.format(voices, swidth);


            //---------------------------------------------------------
            // STEP 3: Create and draw stave
            //---------------------------------------------------------
            const stave = new Vex.Stave(sx, 0, swidth + MARGIN);
            if (bar === 0) {
                stave.addClef(this.clef);
                stave.addTimeSignature(audio.meter);
                if (!this.isPercussion) {
                    stave.addKeySignature(audio.key.signature);
                }
                swidth += stave.getModifierXShift()
                stave.setWidth(swidth + MARGIN);
            }
            stave.setContext(context).draw();


            //---------------------------------------------------------
            // STEP 4: draw each voice with beams
            //---------------------------------------------------------
             for (const v of voices) {
                const beams = Vex.Beam.generateBeams(v.getTickables());
                v.draw(context, stave);
                const box = v.getBoundingBox();
                minY = Math.min(minY, box.y);
                maxY = Math.max(maxY, box.y + box.h);
                beams.forEach(b => { b.setContext(context).draw() });
            }
            sx += swidth + MARGIN;
        }
            

        // draw ties
        const ties = [];
        for (const n of this.notes) {
            if (n.tie) ties.push(new Vex.StaveTie({ firstNote : n.vnote, lastNote : n.tie.vnote }));
        }
        for (const t of ties) t.setContext(context).draw();

        // resize the SVG element base on the minimum and maximum note
        minY = Math.min(0, minY - 15);
        maxY = Math.max(110, maxY + 15);
        const height = maxY - minY;
        renderer.resize(sx + MARGIN, height);


        // vexflow unhelpfully overconstrains the svg element dimensions making it hard to scale
        this.container.querySelector('svg')?.removeAttribute('style');
        this.container.querySelector('svg')?.removeAttribute('width');
        this.container.querySelector('svg')?.removeAttribute('height');
        this.container.querySelector('svg')?.setAttribute('viewBox', `0 ${minY} ${sx + MARGIN} ${height}`);


        // construct the map between beats and notes and x-values
        this.notes.forEach(n => {
            this.beatmap.set(n.time, n.vnote.getBoundingBox().x); 
        });
    }


    /**
     * Highlight notes that intersect with the playhead
     */
    movePlayhead(ph : number) {
        this.playhead = Math.max(0, Math.min(this.beats, ph));
        const svg = this.container.querySelector('svg');
        svg?.querySelectorAll('.vf-stavenote').forEach(el => { el.classList.remove('highlight'); });

        const epsilon = 1/128;
        const slice = this.notes.filter(n => { return (n.time <= ph && (n.end - epsilon) > ph); });

        slice.forEach(n => {
            svg?.querySelector(`#vf-${n.vnote.attrs.id}`)?.classList.add('highlight');
        })
        const delta = this.beatToX(ph) - this.beatToX(0);
        this.scrollContainer.scrollTo(delta - this.width * 0.6, 0);
    }
    private playhead = 0;


    private beatToX(playhead : number) : number {
        let best : number | undefined;
        let bestX = 0;
        for (const [ beat, x ] of this.beatmap) {
            const delta = Math.abs(playhead - beat);
            if (best === undefined || delta < best) {
                best = delta;
                bestX = x;
            }
            if (beat > playhead) break;
        }
        return this.SVGToScreen(bestX);
    }


    private xToBeat(x : number) : number {
        let closestBeat = 0;
        let closestX : number | undefined;
        x = this.screenToSVG(x);
        for (const [ beat, beatX ] of this.beatmap) {
            const delta = Math.abs(x - beatX);
            if (closestX === undefined || delta < closestX) {
                closestX = delta;
                closestBeat = beat;
            }
            if (beatX > x) return closestBeat;
        }
        return closestBeat;
    }


    private generateVoice(measure : MusicTrace, beatCount : number, beatValue : number) : NoteRecord[] {

        // STEP 1: join trace events into chords based on trace_group
        let groups = this.groupIntoChords(measure);

        // STEP 2: split each group into standard time divisions
        groups = this.splitGroups(groups, beatCount, beatValue);
        
        // STEP 3: create vex notes and return
        groups.forEach(rec => rec.createVexNote(this.clef));
        return groups;
    }


    private groupIntoChords(trace : MusicTrace) : NoteRecord[] {
        const groups : NoteRecord[] = [];
        let last : NoteRecord | undefined;

        for (const te of trace) {
            if (last && te.trace_group == last.traceGroup) {
                last.addTraceEvent(te);
            } else {
                last = new NoteRecord();
                last.addTraceEvent(te);
                groups.push(last);
            }
        }
        return groups;
    }


    private splitGroups(groups : NoteRecord[], beatCount : number, beatValue : number) : NoteRecord[] {
        const records : NoteRecord[] = [];
        let beats = 0;
        beatCount *= (4 / beatValue);
 
        for (const group of groups) {
            let curr = group;
            let duration = curr.duration;

            // repeatedly spit notes into standard time durations
            while (duration >= 1/16 && beats < beatCount) {
                records.push(curr);
                if (curr.needsSplit(beatCount - beats)) {
                    let next = curr.split(beatCount - beats);
                    beats += curr.duration;
                    duration -= curr.duration;
                    curr = next;
                } else {
                    curr.round();
                    beats += curr.duration;
                    duration -= curr.duration;
                }
            }

            // carry leftover event to the next measure
            if (beats >= beatCount && duration >= 1/16) {
                this.remainders.push(... curr.tnotes);
            }
        }

        // if needed pad the end of the measure with a rest
        while (beats < beatCount) {
            const rest = new NoteRecord();
            rest.addRestEvent(beats, beatCount - beats);
            rest.round();
            records.push(rest);
            beats += rest.duration;
        }
        return records;
    }


    screenToSVG(screenX : number) : number {
        const svg = this.container.querySelector('svg')!;
        const p = svg.createSVGPoint();
        p.x = screenX;
        p.y = 0;
        return p.matrixTransform(svg.getScreenCTM()!.inverse()).x;
    }

    SVGToScreen(svgX : number) : number {
        const svg = this.container.querySelector('svg');
        if (svg) {
            const p = svg.createSVGPoint()
            p.x = svgX;
            p.y = 0;
            return p.matrixTransform(svg.getScreenCTM()!).x;
        } else {
            return svgX;
        }
    }
}


/**
 * Helper class that groups TraceEvents into chords and generates Vex StaveNotes
 */
class NoteRecord {

    audio : TunePadAudio;
    tnotes : TraceEvent[] = [];
    vnote : any;  // StaveNote
    tie ? : NoteRecord;

    /** target number of beats for this set of events */
    get duration() { return this.tnotes[0].duration; }
    set duration(d : number) { this.tnotes.forEach(t => t.duration = d); }

    get command() { return this.tnotes[0].command }
    get traceGroup() { return this.tnotes[0].trace_group; }

    /** onset of the note event in beats */
    get time() { return this.tnotes[0].time; }

    /** end of the note in beats */
    get end() { return this.tnotes[0].end; }

    constructor() { this.audio = TunePadAudio.init(); }

    private clone() {
        const copy = new NoteRecord();
        this.tnotes.forEach(t => copy.addTraceEvent(t));
        return copy;
    }

    addTraceEvent(tnote : TraceEvent) {
        this.tnotes.push(tnote.clone());
    }

    addRestEvent(time : number, duration : number) {
        const rest = new TraceEvent(TraceEvent.REST, time);
        rest.duration = duration;
        this.tnotes.push(rest);
    }

    /** is there a mismatch between a nominal note division and this duration */
    needsSplit(remaining : number) : boolean {
        const used = getStandardDuration(Math.min(remaining, this.duration));
        return (this.duration - used) >= 1/16;
    }

    /** 
     * split this note into two parts. this part is shortened to the largest standard 
     * note value that can fit in this measure (remaining).
     * the returned part is whatever's leftover
     */
    split(remaining : number) : NoteRecord {
        const used = getStandardDuration(Math.min(remaining, this.duration));
        const next = this.clone();
        next.duration = (this.duration - used);
        this.duration = used;
        this.tie = next;
        return next;
    }

    /**
     * round duration down to the nearest standard note duration
     */
    round() {
        this.duration = getStandardDuration(this.duration);
    }


    createVexNote(clef : string) {
        if (this.command === TraceEvent.REST) {
            this.vnote = toVexRest(this.duration);
        } else {
            this.vnote = this.toVexNote(clef);
        }
    }


    private toVexNote(clef : string, annotate : boolean = true) {
        const isSound = (this.command === TraceEvent.SAMPLE);
        const isPercussion = (clef === 'percussion');
        const key = this.audio.key;
        
        const vexNames : string[] = [];
        const names = this.tnotes.map(tn => {
            const note = tn.note;
            const name = asciiAccidental(key.getNoteName(note));
            if (isSound) {
                return 'b/4';
            } else if (isPercussion) {
                return toPercussionNote(note);
            } else {
                return `${name}/${note.octave - 1}`;
            }
        });
        const beats = this.duration;
        const duration = getVexDuration(beats);
        
        const vn = new Vex.StaveNote({
            clef: isSound ? 'treble' : clef,
            keys: names,
            duration: duration,
            autoStem: true
        });
        if (duration.endsWith('d')) Vex.Dot.buildAndAttach([ vn ]);

        if (isSound) {
            vn.setStyle({fillStyle: "red", strokeStyle: "red"});
        }



        for (let i=0; i<this.tnotes.length; i++) {
            const note = this.tnotes[i].note;
            const acc = asciiAccidental(key.getAccidental(note));

            // don't draw accidentals if the note is already in the current key
            
            if (!isPercussion && !key.containsNote(note) && [ '#', 'b', 'n' ].includes(acc)) {
                vn.addModifier(new Vex.Accidental(acc), i);
            }
        }

        if (annotate && !(isSound || isPercussion) && this.tnotes.length === 1) {
            const note = this.tnotes[0].note;
            const name = asciiAccidental(key.getNoteName(note));
            vn
            .addModifier(new Vex.Annotation(`${name}${note.octave - 1}`)
            .setPosition(Vex.Annotation.Position.RIGHT))
        }

        return vn;
    }
}


function toPercussionNote(note : Note) {
    switch (note.note % 16) {
        case 0: return 'b/3';
        case 1: return 'c/4';
        case 2: return 'd/4';
        case 3: return 'e/4';
        case 4: return 'f/4';
        case 5: return 'g/4';
        case 6: return 'a/4';
        case 7: return 'b/4';
        case 8: return 'c/5';
        case 9: return 'd/5';
        case 10: return 'e/5';
        case 11: return 'f/5';
        case 12: return 'g/5';
        case 13: return 'a/5';
        case 14: return 'b/5';
        case 15: return 'c/6';
        default: return `${note.name}/4`;
    }
}


function toVexRest(beats : number) {
    const duration = getVexDuration(beats);
    const vn = new Vex.StaveNote({ keys: [ 'b/4' ], duration: duration + "r" });
    if (duration.endsWith('d')) Vex.Dot.buildAndAttach( [ vn ]);
    return vn;
}

function getStandardDuration(beats : number) {
    for (const [key, val] of durations) {
        if (approx(beats, key) || beats >= key) return key;
    }
    return 1/16;
}

function getVexDuration(beats : number) {
    for (const [key, val] of durations) {
        if (approx(beats, key) || beats >= key) return val;
    }
    return "64";
}

const durations = new Map<number, string>([
    [ 4, "1" ],
    [ 3, "2d" ],
    [ 2, "2" ],
    [ 1 + 1/2, "4d" ],
    [ 1, "4" ],
    [ 1/2 + 1/4, "8d" ],
    [ 1/2, "8" ],
    [ 1/4 + 1/8, "16d" ],
    [ 1/4, "16" ],
    [ 1/8 + 1/16, "32d" ],
    [ 1/8, "32" ],
    [ 1/16 + 1/32, "64d" ],
    [ 1/16, "64" ]
]);


function bestClef(trace : MusicTrace) {
    const lowCount = noteCountInRange(trace, 0, 59);
    const highCount = noteCountInRange(trace, 60, 127);
    return lowCount > highCount ? 'bass' : 'treble';
}


/**
 * how many notes fall within the given note range?
 */
function noteCountInRange(trace : MusicTrace, low : number, high : number) : number {
    return trace
        .ofType([ TraceEvent.PLAY, TraceEvent.SAMPLE ])
        .filter(t => (t.note.note >= low && t.note.note <= high))
        .length;
}

function approx(a : number, b : number, fudge = 0.001) {
    return Math.abs(a - b) <= fudge;
}

function asciiAccidental(name : string) {
    return name
        .replace('♮', 'n')
        .replace('♭', 'b')
        .replace('♯', '#');
}